// The authentication wall. Login is REQUIRED: an unauthenticated visitor sees only this gate and
// cannot reach the app. Once a session is present, the full app is mounted (dynamic import) and the
// gate fades away.
//
// OFFLINE-FIRST: the gate waits on auth.ready, which resolves once the cached session has been
// restored from localStorage (no network). A returning signed-in user goes straight to the app
// with no network round-trip; only a first-time login needs the network. To avoid a jarring flash,
// the gate holds a neutral splash until that decision is known, then either reveals the login form
// or mounts the app behind the still-covering splash.

import { el } from './dom';
import { auth } from '../auth/store';
import { buildLoginForm } from './account';

// The brandmark: a chart compass rose. Drawn rather than an emoji so it's crisp at any size and
// inherits the accent via currentColor (the 8-point star uses its own teal gradient). Static markup
// → innerHTML is safe. An elongated, lightened north point reads as the bearing the name implies.
export const COMPASS_SVG =
  '<svg viewBox="0 0 100 100" width="100%" height="100%" aria-hidden="true">' +
  '<defs><linearGradient id="sbRose" x1="0" y1="0" x2="0" y2="1">' +
  '<stop offset="0" stop-color="#43d8c8"/><stop offset="1" stop-color="#1c7d75"/>' +
  '</linearGradient></defs>' +
  '<circle cx="50" cy="50" r="47" fill="none" stroke="currentColor" stroke-width="1.4" opacity="0.55"/>' +
  '<circle cx="50" cy="50" r="39" fill="none" stroke="currentColor" stroke-width="0.8" opacity="0.22"/>' +
  '<g stroke="currentColor" stroke-width="1.2" opacity="0.4">' +
  '<line x1="50" y1="3" x2="50" y2="7"/><line x1="73.5" y1="9.3" x2="71.5" y2="12.8"/>' +
  '<line x1="90.7" y1="26.5" x2="87.2" y2="28.5"/><line x1="97" y1="50" x2="93" y2="50"/>' +
  '<line x1="90.7" y1="73.5" x2="87.2" y2="71.5"/><line x1="73.5" y1="90.7" x2="71.5" y2="87.2"/>' +
  '<line x1="50" y1="97" x2="50" y2="93"/><line x1="26.5" y1="90.7" x2="28.5" y2="87.2"/>' +
  '<line x1="9.3" y1="73.5" x2="12.8" y2="71.5"/><line x1="3" y1="50" x2="7" y2="50"/>' +
  '<line x1="9.3" y1="26.5" x2="12.8" y2="28.5"/><line x1="26.5" y1="9.3" x2="28.5" y2="12.8"/>' +
  '</g>' +
  '<polygon points="50,8 53.1,42.6 67,33 57.4,46.9 92,50 57.4,53.1 67,67 53.1,57.4 50,92 46.9,57.4 33,67 42.6,53.1 8,50 42.6,46.9 33,33 46.9,42.6" fill="url(#sbRose)" opacity="0.95"/>' +
  '<polygon points="50,8 53.1,42.6 46.9,42.6" fill="#e9f2f8"/>' +
  '<circle cx="50" cy="50" r="3" fill="#07131c" stroke="currentColor" stroke-width="1"/>' +
  '</svg>';

export class AuthGate {
  readonly root: HTMLElement;
  private host: HTMLElement;
  private booted = false; // app has been mounted at least once this page-load
  private ready = false;

  /** @param mountApp mounts the full app (idempotent); resolves once it's on screen. */
  constructor(private mountApp: () => Promise<void>) {
    const rose = el('div', { class: 'sb-gate-rose' });
    rose.setAttribute('aria-hidden', 'true');
    rose.innerHTML = COMPASS_SVG;

    this.host = el('div', { class: 'sb-gate-host' }, [this.splash('Acquiring position…')]);
    this.root = el('div', { class: 'sb-gate' }, [
      el('div', { class: 'sb-gate-card' }, [
        el('div', { class: 'sb-gate-brand' }, [
          rose,
          el('div', { class: 'sb-gate-name' }, ['Seabearing']),
          el('div', { class: 'sb-gate-tag' }, ['Anchorage charts from real bathymetry.']),
        ]),
        this.host,
      ]),
    ]);

    // Decide only once the initial (cached) session is known — never flash the form first.
    void auth.ready.then(() => { this.ready = true; this.refresh(); });
    auth.subscribe(() => { if (this.ready) this.refresh(); });
  }

  private refresh(): void {
    if (auth.snapshot.user) void this.toAuthed();
    else this.toLogin();
  }

  private async toAuthed(): Promise<void> {
    if (this.booted) { this.conceal(); return; }
    this.booted = true;
    // Keep the gate covering with a splash while the app builds, so there's no flash of a
    // half-loaded map under a disappearing form.
    this.host.replaceChildren(this.splash('Plotting charts…'));
    this.root.classList.remove('sb-gate-login');
    await this.mountApp();
    this.conceal();
  }

  private toLogin(): void {
    // Signed out AFTER the app was already running (an in-app sign-out): reload for a clean reset
    // back to the wall rather than tearing down a live app in place.
    if (this.booted) { window.location.reload(); return; }
    this.host.replaceChildren(buildLoginForm(''));
    this.root.classList.add('sb-gate-login');
    this.reveal();
  }

  private conceal(): void {
    this.root.classList.add('sb-gate-hidden');
  }
  private reveal(): void {
    this.root.classList.remove('sb-gate-hidden');
  }

  private splash(text: string): HTMLElement {
    return el('div', { class: 'sb-gate-splash' }, [el('span', { class: 'sb-spinner' }, [text])]);
  }
}
